import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from './ui/components';
import { Icon } from './ui/icons';
import { AuthShowcase, Eyebrow } from './pages/auth-redesign/showcase';

// Hand-off page for the emailed "Open in the desktop app" button: /open-app/:token
//
// That button used to point straight at tableview://sign-in/TOKEN, and mail clients
// dropped it. They strip hrefs whose scheme they do not recognise — Gmail leaves the
// anchor as plain, unclickable text — so the emailed link has to be ordinary https and
// the jump to the custom scheme has to happen from a page the browser already trusts.
// That is all this is.
//
// It deliberately does not touch the token: no peek, no consume. Links are single-use,
// so spending one here would mean the app receives a token that is already dead.
const OpenInApp = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [tried, setTried] = useState(false);
  const firedRef = useRef(false);

  const openApp = useCallback(() => {
    if (!token) return;
    setTried(true);
    window.location.href = `tableview://sign-in/${token}`;
  }, [token]);

  useEffect(() => {
    if (firedRef.current || !token) return;
    firedRef.current = true; // one token, one automatic attempt (StrictMode double-mount guard)
    openApp();
  }, [token, openApp]);

  if (!token) {
    return (
      <AuthShowcase>
        <div className="authx-form">
          <header className="authx-form__head">
            <Eyebrow>Sign-in link</Eyebrow>
            <h1 className="authx-form__title">That link is incomplete</h1>
            <p className="authx-form__sub">Ask for a new sign-in link and open it from your inbox.</p>
          </header>
          <div className="authx-form__actions">
            <Button
              type="button"
              variant="primary"
              fullWidth
              size="lg"
              leadingIcon={<Icon.ArrowRight size={16} />}
              onClick={() => navigate('/login', { replace: true })}
            >
              Back to sign in
            </Button>
          </div>
        </div>
      </AuthShowcase>
    );
  }

  return (
    <AuthShowcase>
      <div className="authx-form">
        <header className="authx-form__head">
          <Eyebrow>Desktop app</Eyebrow>
          <h1 className="authx-form__title">Opening the TableView app</h1>
          <p className="authx-form__sub">
            {tried
              ? 'Your browser should be asking whether to open TableView. Say yes and you will be signed in there. Nothing has been used up — if the prompt did not appear, try again.'
              : 'Handing your sign-in over to the desktop app.'}
          </p>
        </header>

        <div className="authx-form__actions">
          <Button
            type="button"
            variant="primary"
            fullWidth
            size="lg"
            leadingIcon={<Icon.AppWindow size={16} />}
            onClick={openApp}
          >
            Open the desktop app
          </Button>
          <Button
            type="button"
            variant="link"
            fullWidth
            onClick={() => navigate(`/sign-in/${token}`, { replace: true })}
          >
            Sign in in this browser instead
          </Button>
        </div>
      </div>
    </AuthShowcase>
  );
};

export default OpenInApp;
